import React, { ReactNode, useEffect } from 'react';
import { Form, Button, Table, Row, Col } from 'react-bootstrap';
import { Link, useForm, usePage } from '@inertiajs/react';

import AuthedLayout from '../../Layouts/Authed';
import Valuation from '../../Data/Valuation';
import CarrierLossRatio from '../../Data/CarrierLossRatio';
import CarrierLossRatioYear from '../../Data/CarrierLossRatioYear';
import { getEventValue, replaceNullWithEmpty } from './Helpers/RepeatingForms';
import { range } from '../../utils';
import FormHeader from '../../Components/Forms/FormHeader';
import CarrierLossRatioRow from './CarrierLossRatios/CarrierLossRatioRow';
import DecimalInput from '../../Components/Forms/DecimalInput';
import Tooltip from '../../Components/Forms/Tooltip';
import AutosaveStatus from '../../Components/Forms/AutosaveStatus';
import useValuationStepAutosave from './Helpers/useValuationStepAutosave';
import MarkComplete from '../../Components/Forms/MarkComplete';

interface Props {
	valuation: Valuation;
	carrier_loss_ratios: CarrierLossRatio[];
	maxCarrierLossRatiosPerValuation: number;
	currentYear: number;
}

interface FormData {
	total_agency_loss_ratio: number;
	carrier_loss_ratios: Partial<CarrierLossRatio>[];
	mark_complete: boolean;
}

const CarrierLossRatios = (props: Props) => {
	const { errors, readOnly } = usePage<any>().props;

	const { data, setData, post, processing } = useForm<FormData>({
		total_agency_loss_ratio: props.valuation.total_agency_loss_ratio ?? '',
		carrier_loss_ratios:
			replaceNullWithEmpty(props.carrier_loss_ratios) ?? [],
		mark_complete: props.valuation.completed_steps?.includes('carrier-loss-ratios') ?? false,
	});
	const autosaveStatus = useValuationStepAutosave('carrier-loss-ratios', props.valuation.id, data);

	const submit = (event: any) => {
		event.preventDefault();
		post(`/valuation-form/${props.valuation.id}/carrier-loss-ratios`);
	};

	const updateIndex = (event: React.ChangeEvent<any>, index: number) => {
		const newData = JSON.parse(JSON.stringify(data));
		newData.carrier_loss_ratios[index][event.target.name] =
			getEventValue(event);
		setData(newData);
	};

	const updateYear = (
		index: number,
		yearIndex: number,
		event: React.ChangeEvent<any>
	) => {
		const newData = JSON.parse(JSON.stringify(data));
		newData.carrier_loss_ratios[index].years[yearIndex][event.target.name] =
			getEventValue(event);
		setData(newData);
	};

	useEffect(() => {
		scrollTo(0,0);
	}, []);

	return (
		<div>
			<FormHeader
				agencyName={props.valuation.agency_name}
				effectiveDate={props.valuation.effective_date}
				pageName='Loss Ratio By Carrier - Top 5 Carriers'
			/>
			<Form onSubmit={submit} className='valuationForm'>
				<fieldset disabled={readOnly}>
				<Row className='py-4'>
					<Col md={4}>
						<DecimalInput
							name='total_agency_loss_ratio'
							label={
								<>
									What is your loss ratio for your five largest carriers?
									<Tooltip id='total-loss-ratio'>
										Use your best estimate. Check your AMS if they provide an agency-wide loss ratio and if so use that here.
									</Tooltip>
								</>
							}
							prefix={null}
							postfix='%'
							negativeValues={true}
							value={data.total_agency_loss_ratio ?? ''}
							onChange={(event) =>
								setData({
									...data,
									total_agency_loss_ratio: getEventValue(event),
								})
							}
							errors={errors}
							arrayName='valuation.total_agency_loss_ratio'
							errorArrayName='valuation.total_agency_loss_ratio'
							overrideArrayName={true}
							overrideErrorArrayName={true}
						/>
					</Col>
				</Row>
				<Table striped borderless hover responsive>
					<thead>
						<tr>
							<th
								id='carrierLossRatioNum'
								className='cell-xs sticky-cell'
							>
								#
							</th>
							<th id='carrier_name' className='cell-lg'>
								Carrier Name
							</th>
							{range(
								props.currentYear - 5,
								props.currentYear
							).map((year) => (
								<th id={`${year}-loss_ratio`} key={year}>
									{year}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{data.carrier_loss_ratios.map((lossRatio, index) => (
							<CarrierLossRatioRow
								key={`carrierLossRatio-${index}`}
								currentYear={props.currentYear}
								lossRatio={lossRatio}
								index={index}
								errors={errors}
								updateIndex={updateIndex}
								updateYear={updateYear}
							/>
						))}
					</tbody>
				</Table>
				</fieldset>

				<div className='d-flex justify-content-between align-items-center'>
					<Button
						as={Link as any}
						href={`/valuation-form/${props.valuation.id}/agency-information`}
						variant='outline-primary'
						className='mt-4'
					>
						Back
					</Button>
					<div className='d-flex align-items-center'>
						{!readOnly && <AutosaveStatus status={autosaveStatus} />}
						{!readOnly && (
							<MarkComplete
								checked={data.mark_complete}
								onChange={(checked) => setData('mark_complete', checked)}
								className='mt-4 ms-4'
							/>
						)}
						{readOnly ? (
							<Button
								as={Link as any}
								href={`/valuation-form/${props.valuation.id}/carrier-retention-rates`}
								className='mt-4 ms-4'
							>
								Next
							</Button>
						) : (
							<Button type='submit' className='mt-4 ms-4'>
								Next
							</Button>
						)}
					</div>
				</div>
			</Form>
		</div>
	);
};

CarrierLossRatios.layout = (page: ReactNode) => (
	<AuthedLayout>{page}</AuthedLayout>
);

export default CarrierLossRatios;
